import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import CtaBand from "@/components/CtaBand";
import Seo from "@/components/Seo";

const WELD_IMG = "/work/vessel-handover.jpg";

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About SAMI FAB | Aluminium Fabricator Albany WA"
        description="Learn about SAMI FAB, a local Albany WA aluminium welding and fabrication business specialising in boat modifications, marine work and custom builds across the Great Southern."
        keywords="about SAMI FAB, aluminium fabricator Albany WA, local boat fabricator, marine fabrication Albany, Great Southern fabrication business"
        path="/about"
      />
      <section data-testid="about-page-header" className="relative overflow-hidden pt-[76px] bg-white">
        <div className="grid-drift" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="overflow-hidden">
              <motion.p
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#007bff]"
              >
                About — Sami Fab, Albany WA
              </motion.p>
            </div>
            <div className="overflow-hidden mt-4">
              <motion.h1
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.22 }}
                className="font-display font-black uppercase tracking-tighter text-[#003366] text-4xl sm:text-5xl lg:text-6xl leading-[0.98]"
              >
                Proudly Albany.<br />Built for the<br />Great Southern.
              </motion.h1>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
              className="mt-7 text-base md:text-lg leading-relaxed text-slate-600"
              data-testid="about-story"
            >
              Sami Fab is a local aluminium welding and fabrication business based in Albany, WA,
              proudly servicing the Great Southern. Boats are the specialty — but the same care goes
              into every toolbox, canopy, trailer and caravan accessory that leaves the
              workshop. When you get in touch, you deal directly with your fabricator — from the
              first sketch to the final weld.
            </motion.p>
          </div>
          <div>
            <motion.div
              initial={{ clipPath: "inset(0 0 100% 0)" }}
              animate={{ clipPath: "inset(0 0 0% 0)" }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.35 }}
              className="relative border border-[#007bff]/45 overflow-hidden aspect-[4/3] group"
              data-testid="about-image-frame"
            >
              <img
                src={WELD_IMG}
                alt="Paul from SAMI FAB on a commercial vessel after an aluminium boat fit-out in Albany WA"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.52 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <a
                href="tel:0415464900"
                data-testid="about-call-cta"
                className="inline-flex items-center gap-3 bg-[#343a40] text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#007bff] hover:scale-[1.03] transition-[background-color,transform] duration-200"
              >
                <Phone size={16} /> Call Paul
              </a>
              <Link
                to="/quote"
                data-testid="about-quote-cta"
                className="group inline-flex items-center gap-3 border border-[#343a40] text-[#343a40] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:border-[#007bff] hover:text-[#007bff] transition-colors duration-200"
              >
                Free Quote
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
